// ========================================
// components/admin/Reports.js - Admin Reports
// ========================================
// PURPOSE: Generate and export reports for admin
// Reports: Users, Assets, Unclaimed Assets, Plan Distribution
// ========================================

import React, { useState } from 'react';
import axios from 'axios';

const Reports = () => {
  const [loading, setLoading] = useState({
    users: false,
    assets: false,
    unclaimed: false,
    plans: false
  });
  const [error, setError] = useState('');

  // ========================================
  // DOWNLOAD REPORT
  // ========================================
  const downloadReport = async (reportType, filename) => {
    setLoading(prev => ({ ...prev, [reportType]: true }));
    setError('');
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/reports/${reportType}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filename}-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError(`Failed to download ${reportType} report`);
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, [reportType]: false }));
    }
  };

  const reportCards = [
    {
      id: 'users',
      title: '👥 Users Report',
      description: 'Export all registered users with their plan, status, and activity',
      icon: '👥',
      color: '#2E7D32',
      filename: 'agyapadie-users'
    },
    {
      id: 'assets',
      title: '📦 Assets Report',
      description: 'Export all assets with owner, type, value, and certificate info',
      icon: '📦',
      color: '#FFD700',
      filename: 'agyapadie-assets'
    },
    {
      id: 'unclaimed',
      title: '🏦 Unclaimed Assets Report',
      description: 'Export all unclaimed assets with deceased owner details',
      icon: '🏦',
      color: '#dc2626',
      filename: 'agyapadie-unclaimed'
    },
    {
      id: 'plans',
      title: '📊 Plan Distribution Report',
      description: 'Export plan usage statistics',
      icon: '📊',
      color: '#1A1A1A',
      filename: 'agyapadie-plans'
    }
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>📋 Reports & Exports</h2>
        <p style={styles.subtitle}>
          Generate CSV reports for analysis and record keeping
        </p>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.reportsGrid}>
        {reportCards.map((report) => (
          <div key={report.id} style={styles.reportCard}>
            <div style={{...styles.reportIcon, backgroundColor: report.color}}>
              <span style={styles.reportIconText}>{report.icon}</span>
            </div>
            <div style={styles.reportContent}>
              <h3 style={styles.reportTitle}>{report.title}</h3>
              <p style={styles.reportDescription}>{report.description}</p>
              <button
                onClick={() => downloadReport(report.id, report.filename)}
                disabled={loading[report.id]}
                style={styles.downloadButton}
              >
                {loading[report.id] ? 'Generating...' : '📥 Download CSV'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div style={styles.infoBox}>
        <h3 style={styles.infoTitle}>ℹ️ About Reports</h3>
        <p style={styles.infoText}>
          All reports are exported as CSV files that can be opened in Microsoft Excel, Google Sheets, or any spreadsheet software.
        </p>
        <p style={styles.infoText}>
          Reports include all data up to the current moment and are generated on demand.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '20px',
    backgroundColor: '#F5F0E6',
    minHeight: '100vh'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '24px',
    color: '#1A1A1A',
    marginBottom: '10px'
  },
  subtitle: {
    fontSize: '14px',
    color: '#666'
  },
  errorBox: {
    backgroundColor: '#fee2e2',
    border: '1px solid #ef4444',
    color: '#dc2626',
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '20px'
  },
  reportsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginBottom: '30px'
  },
  reportCard: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    display: 'flex',
    gap: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s',
    ':hover': {
      transform: 'translateY(-2px)'
    }
  },
  reportIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  reportIconText: {
    fontSize: '28px'
  },
  reportContent: {
    flex: 1
  },
  reportTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    marginBottom: '8px'
  },
  reportDescription: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '15px',
    lineHeight: '1.4'
  },
  downloadButton: {
    padding: '8px 16px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 'bold',
    transition: 'all 0.3s',
    ':hover': {
      backgroundColor: '#B8860B'
    }
  },
  infoBox: {
    backgroundColor: '#e8f4fd',
    border: '1px solid #2E7D32',
    borderRadius: '10px',
    padding: '20px'
  },
  infoTitle: {
    fontSize: '16px',
    color: '#1A1A1A',
    marginBottom: '10px'
  },
  infoText: {
    fontSize: '14px',
    color: '#1A1A1A',
    marginBottom: '8px',
    lineHeight: '1.5'
  }
};

export default Reports;